import type { Activity } from './schemas/activity.js';
import type { Task } from './schemas/task.js';
import type { Vendor } from './schemas/vendor.js';
import { taskDigest } from './tasks.js';
import { upcomingPayments } from './vendors.js';

/**
 * What the bell shows: what the other person did, and what is about to bite.
 *
 * Two different things on purpose. Activity is a record of something that
 * happened; a deadline is a fact about now that nobody did. Both are reasons to
 * look at the app, and a bell that only showed one of them would be half a bell.
 */

export interface NotificationItem {
  at: string;
  userId: string;
  action: string;
  entity: string;
  entityId: string;
  summary?: string;
}

export interface DueItem {
  kind: 'task' | 'payment';
  title: string;
  dueDate: string;
  overdue: boolean;
  /** For a payment: who it is owed to. */
  context?: string;
}

export interface Notifications {
  items: NotificationItem[];
  /** Things other people did that you have not looked at yet. */
  unread: number;
  due: DueItem[];
}

/**
 * **Your own actions are never shown.**
 *
 * You know you added the florist; being told so is noise, and worse, it makes the
 * unread count go up every time you use the app — which is exactly how people
 * learn to ignore a badge.
 */
export function buildNotifications(input: {
  activity: readonly Activity[];
  me: string;
  /** When this person last opened the bell. Absent means never. */
  seenAt?: string;
  tasks: readonly Task[];
  vendors: readonly Vendor[];
  today: string;
  limit?: number;
  /** How far ahead a deadline counts as worth mentioning. */
  dueWithinDays?: number;
}): Notifications {
  const theirs = input.activity
    .filter((a) => a.userId !== input.me)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const items = theirs.slice(0, input.limit ?? 30).map((a) => ({
    at: a.createdAt,
    userId: a.userId,
    action: a.action,
    entity: a.entity,
    entityId: a.entityId,
    ...(a.summary ? { summary: a.summary } : {}),
  }));

  const unread = input.seenAt
    ? theirs.filter((a) => a.createdAt > (input.seenAt as string)).length
    : theirs.length;

  const within = input.dueWithinDays ?? 14;
  const digest = taskDigest(input.tasks, input.today, within);

  const due: DueItem[] = [
    ...digest.overdue.map((t) => ({
      kind: 'task' as const,
      title: t.title,
      dueDate: t.dueDate as string,
      overdue: true,
    })),
    ...digest.dueSoon.map((t) => ({
      kind: 'task' as const,
      title: t.title,
      dueDate: t.dueDate as string,
      overdue: false,
    })),
    ...upcomingPayments([...input.vendors], {
      today: new Date(`${input.today}T00:00:00Z`),
      withinDays: within,
    }).map((p) => ({
      kind: 'payment' as const,
      title: p.payment.label,
      dueDate: p.payment.dueDate as string,
      overdue: p.overdue,
      context: p.vendorName,
    })),
  ]
    // Late first, then soonest. A bell sorted by anything else buries the one
    // thing that needed attention yesterday.
    .sort((a, b) => Number(b.overdue) - Number(a.overdue) || a.dueDate.localeCompare(b.dueDate))
    .slice(0, 20);

  return { items, unread, due };
}

/** True when the bell has anything worth a dot. */
export const hasNotifications = (n: Notifications): boolean =>
  n.unread > 0 || n.due.some((d) => d.overdue);
